/**
 * Custodia del token di accesso.
 *
 * Strategia (richiesta dal capitolato del back-office):
 *
 * * **il client HTTP legge il token da una variabile di modulo**, non dallo
 *   storage, e la variabile sparisce alla chiusura della scheda;
 * * **su `localStorage` resta un promemoria di sessione** (token, scadenza,
 *   profilo) che serve a *ripristinare* la sessione dopo un ricaricamento della
 *   pagina, evitando di richiedere di nuovo le credenziali a ogni F5.
 *
 * ## Che cosa questa custodia NON fa
 *
 * **Non protegge il token da XSS**, ed e' importante non credere il contrario.
 * Il promemoria contiene il token *per intero e in chiaro*: uno script iniettato
 * in questa origine lo legge da `localStorage` esattamente come leggerebbe
 * qualunque altra chiave. La variabile in memoria non aggiunge alcuna
 * protezione finche' una copia persistente esiste — riduce la superficie solo
 * nel senso che il client non rilegge lo storage a ogni richiesta.
 *
 * Questo commento e' stato riscritto perche' diceva il contrario, e un commento
 * che promette una difesa inesistente e' peggio di nessun commento: il prossimo
 * lettore non va a controllare.
 *
 * La difesa vera contro l'esfiltrazione del token e' non persisterlo: un cookie
 * `httpOnly` con `SameSite=Strict`, oppure un token di accesso breve tenuto in
 * memoria piu' un refresh token in cookie. Entrambe richiedono un endpoint che
 * CONVENTIONS §5 oggi non prevede. Fino ad allora la difesa effettiva e' la CSP
 * di `deploy/nginx-sicurezza.inc`, che vincola gli script all'origine e non
 * ammette `'unsafe-inline'` su `script-src`: e' quella a rendere difficile
 * l'iniezione, non questo modulo.
 *
 * ## Ripristino
 *
 * Il ripristino non e' mai considerato affidabile: all'avvio l'applicazione
 * rimette il token in memoria e poi lo **verifica contro `GET /auth/me`**. Se il
 * server risponde 401 il promemoria viene buttato. La scadenza `scade_il`
 * arriva dall'API gia' formattata `gg/mm/yyyy hh:mm` (CONVENTIONS §2) e viene
 * interpretata qui solo per scartare in anticipo un promemoria gia' scaduto.
 *
 * Nota di contratto: CONVENTIONS §5 non prevede oggi un endpoint di refresh.
 * Quando l'API lo esporra', l'unico punto da toccare e' `rinnovaSessione` nel
 * client: la forma della custodia non cambia.
 */

import type { RispostaAccesso, UtenteCorrente } from "./types";

/** Chiave del promemoria di sessione su `localStorage`. */
const CHIAVE_PROMEMORIA = "trasparenza.backoffice.sessione";

/**
 * Versione del formato del promemoria: se cambia, i vecchi vanno scartati.
 *
 * 2: l'utente porta `nome_utente`. Un promemoria precedente ne e' privo e
 * l'intestazione lo leggerebbe come assente: si torna alla schermata di accesso.
 */
const VERSIONE_PROMEMORIA = 2;

/** Forma serializzata del promemoria di sessione. */
interface PromemoriaSessione {
  versione: number;
  access_token: string;
  scade_il: string;
  utente: UtenteCorrente;
}

/** Il token, in memoria: unica copia usata per firmare le richieste. */
let tokenInMemoria: string | null = null;

/**
 * Chiave del promemoria della sessione **sospesa** durante un'impersonificazione.
 *
 * L'amministratore che si mette nei panni di un'altra utenza deve poter tornare
 * nei propri, e il suo token non puo' vivere nella stessa chiave di quello
 * impersonificato: si sovrascriverebbero, e l'unica via di ritorno sarebbe
 * rifare l'accesso. Due chiavi distinte, una pila di profondita' uno — non si
 * impersonifica da dentro un'impersonificazione, e il server non lo consente
 * comunque (un redattore non puo' chiederla).
 */
const CHIAVE_SOSPESA = "trasparenza.backoffice.sessione-sospesa";

/** Ascoltatori notificati quando la sessione decade (401 o logout esplicito). */
const ascoltatoriDecadenza = new Set<() => void>();

/**
 * Converte `gg/mm/yyyy hh:mm` in millisecondi epoch, o `null` se illeggibile.
 *
 * L'API formatta l'istante in **ora italiana** (`schemas/comuni.py`), mentre
 * `new Date(...)` lo ricostruisce nel fuso del browser: su una postazione fuori
 * fuso le due letture divergono dell'offset. E' accettabile perche' il valore
 * serve solo a scartare in anticipo un promemoria palesemente scaduto, e chi
 * decide davvero e' il 401 del server — ma chi tocca questa funzione deve
 * sapere che non sta leggendo un istante assoluto.
 */
function scadenzaInMillisecondi(scadeIl: string): number | null {
  const corrispondenza = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/.exec(scadeIl.trim());
  if (!corrispondenza) return null;
  const [, giorno, mese, anno, ore = "23", minuti = "59"] = corrispondenza;
  const istante = new Date(
    Number(anno),
    Number(mese) - 1,
    Number(giorno),
    Number(ore),
    Number(minuti),
  );
  return Number.isNaN(istante.getTime()) ? null : istante.getTime();
}

/** Legge il promemoria da `localStorage`, scartandolo se malformato o scaduto. */
function leggiPromemoria(): PromemoriaSessione | null {
  let grezzo: string | null = null;
  try {
    grezzo = window.localStorage.getItem(CHIAVE_PROMEMORIA);
  } catch {
    // Storage negato (modalita' privata, policy del browser): si prosegue senza.
    return null;
  }
  if (!grezzo) return null;

  try {
    const promemoria = JSON.parse(grezzo) as PromemoriaSessione;
    if (
      promemoria.versione !== VERSIONE_PROMEMORIA ||
      typeof promemoria.access_token !== "string" ||
      !promemoria.utente
    ) {
      dimenticaSessione();
      return null;
    }
    const scadenza = scadenzaInMillisecondi(promemoria.scade_il);
    if (scadenza !== null && scadenza <= Date.now()) {
      dimenticaSessione();
      return null;
    }
    return promemoria;
  } catch {
    dimenticaSessione();
    return null;
  }
}

/** Registra la sessione appena aperta: token in memoria, promemoria su storage. */
export function apriSessione(risposta: RispostaAccesso): void {
  tokenInMemoria = risposta.access_token;
  const promemoria: PromemoriaSessione = {
    versione: VERSIONE_PROMEMORIA,
    access_token: risposta.access_token,
    scade_il: risposta.scade_il,
    utente: risposta.utente,
  };
  try {
    window.localStorage.setItem(CHIAVE_PROMEMORIA, JSON.stringify(promemoria));
  } catch {
    // Sessione comunque valida per questa scheda: sara' persa al ricaricamento.
  }
}

/**
 * Rimette in memoria il token del promemoria e restituisce il profilo salvato.
 *
 * Il profilo va considerato **provvisorio** finche' `GET /auth/me` non conferma:
 * ruolo e nodi assegnati possono essere cambiati lato server.
 */
export function ripristinaSessione(): UtenteCorrente | null {
  const promemoria = leggiPromemoria();
  if (!promemoria) return null;
  tokenInMemoria = promemoria.access_token;
  return promemoria.utente;
}

/** Aggiorna il profilo memorizzato dopo una conferma da `GET /auth/me`. */
export function aggiornaProfiloMemorizzato(utente: UtenteCorrente): void {
  const promemoria = leggiPromemoria();
  if (!promemoria) return;
  try {
    window.localStorage.setItem(
      CHIAVE_PROMEMORIA,
      JSON.stringify({ ...promemoria, utente } satisfies PromemoriaSessione),
    );
  } catch {
    // Ininfluente: il profilo in memoria e' comunque quello aggiornato.
  }
}

/** Token corrente, o `null` se non c'e' sessione. */
export function tokenCorrente(): string | null {
  return tokenInMemoria;
}

/** Cancella token e promemoria e avvisa gli ascoltatori. */
export function chiudiSessione(): void {
  tokenInMemoria = null;
  dimenticaSessione();
  for (const ascoltatore of ascoltatoriDecadenza) ascoltatore();
}

/** Cancella il solo promemoria, senza notificare. */
function dimenticaSessione(): void {
  try {
    window.localStorage.removeItem(CHIAVE_PROMEMORIA);
    // Anche la sessione messa da parte: un'uscita deve lasciare il posto
    // pulito, altrimenti il prossimo accesso troverebbe i panni di qualcun
    // altro in attesa di essere ripresi.
    window.localStorage.removeItem(CHIAVE_SOSPESA);
  } catch {
    // Nulla da fare: lo storage non e' disponibile.
  }
}

/**
 * Registra un ascoltatore per la decadenza della sessione.
 * Restituisce la funzione di disiscrizione.
 */
export function osservaDecadenzaSessione(ascoltatore: () => void): () => void {
  ascoltatoriDecadenza.add(ascoltatore);
  return () => {
    ascoltatoriDecadenza.delete(ascoltatore);
  };
}

// --------------------------------------------------------------------------- //
// Impersonificazione
// --------------------------------------------------------------------------- //

/**
 * Passa ai panni di un'altra utenza, conservando la propria sessione.
 *
 * La sessione corrente non viene chiusa ma **messa da parte**: e' cio' che
 * rende il ritorno un gesto e non un nuovo accesso. Se lo storage non e'
 * disponibile la si rifiuta invece di procedere: entrare senza poter tornare
 * sarebbe la peggiore delle due.
 */
export function iniziaImpersonificazione(risposta: RispostaAccesso): boolean {
  const propria = leggiPromemoria();
  if (!propria) return false;
  try {
    window.localStorage.setItem(CHIAVE_SOSPESA, JSON.stringify(propria));
  } catch {
    return false;
  }
  apriSessione(risposta);
  return true;
}

/** Vero se c'e' una sessione messa da parte a cui tornare. */
export function eImpersonificazioneInCorso(): boolean {
  try {
    return window.localStorage.getItem(CHIAVE_SOSPESA) !== null;
  } catch {
    return false;
  }
}

/**
 * Torna alla propria utenza e restituisce il profilo ripristinato.
 *
 * `null` se non c'era nulla da ripristinare: in quel caso chi chiama deve
 * chiudere la sessione, perche' restare nei panni altrui senza saperlo e'
 * peggio di un accesso da rifare.
 */
export function terminaImpersonificazione(): UtenteCorrente | null {
  let grezzo: string | null = null;
  try {
    grezzo = window.localStorage.getItem(CHIAVE_SOSPESA);
  } catch {
    return null;
  }
  if (!grezzo) return null;
  try {
    const propria = JSON.parse(grezzo) as PromemoriaSessione;
    window.localStorage.setItem(CHIAVE_PROMEMORIA, grezzo);
    window.localStorage.removeItem(CHIAVE_SOSPESA);
    tokenInMemoria = propria.access_token;
    return propria.utente;
  } catch {
    try {
      window.localStorage.removeItem(CHIAVE_SOSPESA);
    } catch {
      // Nulla da fare.
    }
    return null;
  }
}
